import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { MapPin, Building2, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  getDioceses,
  getEcoleFacets,
  searchEcoles,
  type EtablissementSearchResult,
} from "@/lib/api";

const PAGE_SIZE = 50; // plafond backend (MAX_PAGE_SIZE)

/** Libellés lisibles pour les clés de catégorie (gestion_type) renvoyées par l'API. */
const CATEGORIE_LABELS: Record<string, string> = {
  DIOCESAINE: "Diocésaine",
  CONGREGANISTE: "Congréganiste",
  PARTENARIAT: "Partenariat",
  CEREAO: "CEREAO",
  AUTRE: "Autre",
  UNKNOWN: "Non renseignée",
};

/** Libellés lisibles pour les clés de cycle renvoyées par l'API. */
const CYCLE_LABELS: Record<string, string> = {
  PRESCOLAIRE: "Préscolaire",
  PRIMAIRE: "Primaire",
  SECONDAIRE_1: "Secondaire 1er cycle",
  SECONDAIRE_2: "Secondaire 2nd cycle",
  SECONDAIRE_1_2: "Secondaire 1er & 2nd cycle",
  SECONDAIRE_GENERAL: "Secondaire général",
  SECONDAIRE_TECHNIQUE: "Secondaire technique",
  PROFESSIONNEL: "Professionnel",
  INSTITUT_SUPERIEUR: "Institut supérieur",
  UNKNOWN: "Non renseigné",
};

function labelFor(map: Record<string, string>, key: string): string {
  return map[key] ?? key;
}

/** Transforme une Map<clé,count> en lignes triées par count décroissant. */
function facetRows(
  facet: Record<string, number> | undefined,
  labels: Record<string, string>,
): Array<{ key: string; label: string; count: number }> {
  if (!facet) return [];
  return Object.entries(facet)
    .map(([key, count]) => ({ key, label: labelFor(labels, key), count }))
    .sort((a, b) => b.count - a.count);
}

export default function EcolesList() {
  const [search, setSearch] = useState("");
  const [categorie, setCategorie] = useState<string | null>(null);
  const [cycle, setCycle] = useState<string | null>(null);

  const { data: dioceses } = useQuery({
    queryKey: ["dioceses"],
    queryFn: () =>
      getDioceses().catch(() => [] as Awaited<ReturnType<typeof getDioceses>>),
  });

  const activeDiocese = dioceses?.find((d) => d.actif)?.slug;

  // Facettes nationales d'emblée (pas de gate sur activeDiocese) : on n'envoie le
  // diocèse que s'il existe. Les facettes reflètent la recherche/diocèse courants.
  const { data: facets } = useQuery({
    queryKey: ["ecoles", "facets", { diocese: activeDiocese, q: search }],
    queryFn: () =>
      getEcoleFacets({ diocese: activeDiocese, q: search }).catch(
        () => undefined,
      ),
  });

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: [
      "ecoles",
      "search",
      { q: search, diocese: activeDiocese, categorie, cycle },
    ],
    queryFn: ({ pageParam = 0 }) =>
      searchEcoles({
        q: search,
        diocese: activeDiocese,
        categorie: categorie ?? undefined,
        cycle: cycle ?? undefined,
        page: pageParam,
        size: PAGE_SIZE,
      }).catch(
        () =>
          ({
            items: [],
            total: 0,
            page: pageParam,
            size: PAGE_SIZE,
          }) as EtablissementSearchResult,
      ),
    initialPageParam: 0,
    getNextPageParam: (_lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.items.length, 0);
      const total = allPages[0]?.total ?? 0;
      return loaded < total ? allPages.length : undefined;
    },
  });

  const items = useMemo(
    () => (data?.pages ?? []).flatMap((p) => p.items),
    [data],
  );
  const total = data?.pages?.[0]?.total ?? 0;

  const categorieRows = facetRows(facets?.parCategorie, CATEGORIE_LABELS);
  const cycleRows = facetRows(facets?.parCycle, CYCLE_LABELS);

  return (
    <>
      {/* Bannière pleine largeur — élèves en uniforme */}
      <div className="relative w-full h-56 md:h-64 overflow-hidden bg-navy">
        <img
          src="/images/ecoles-banner.webp"
          alt="Élèves des écoles catholiques d'Abidjan"
          className="absolute inset-0 w-full h-full object-cover object-center"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/80 via-navy/50 to-navy/20" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 h-full flex flex-col justify-center">
          <h1 className="text-3xl md:text-5xl font-display font-bold text-white mb-2">
            Trouvez l'école de votre enfant
          </h1>
          <p className="text-white/85 text-base md:text-lg max-w-xl">
            Le réseau diocésain : maternelle, primaire, secondaire et institut supérieur.
          </p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-[250px_1fr] gap-8">
      <aside className="space-y-8">
        <div>
          <h3 className="font-bold text-navy mb-4">Recherche</h3>
          <label htmlFor="ecole-search" className="sr-only">
            Rechercher une école par son nom
          </label>
          <Input
            id="ecole-search"
            placeholder="Nom de l'école..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-white border-orange-100"
          />
        </div>

        {categorieRows.length > 0 && (
          <fieldset>
            <legend className="font-bold text-navy mb-4">Catégorie</legend>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                <input
                  type="radio"
                  name="categorie"
                  className="text-coral focus:ring-coral"
                  checked={categorie === null}
                  onChange={() => setCategorie(null)}
                />
                Toutes les catégories
              </label>
              {categorieRows.map((c) => (
                <label
                  key={c.key}
                  className="flex items-center gap-2 text-sm text-navy cursor-pointer"
                >
                  <input
                    type="radio"
                    name="categorie"
                    className="text-coral focus:ring-coral"
                    checked={categorie === c.key}
                    onChange={() => setCategorie(c.key)}
                  />
                  {c.label} ({c.count})
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {cycleRows.length > 0 && (
          <fieldset>
            <legend className="font-bold text-navy mb-4">Cycle</legend>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                <input
                  type="radio"
                  name="cycle"
                  className="text-coral focus:ring-coral"
                  checked={cycle === null}
                  onChange={() => setCycle(null)}
                />
                Tous les cycles
              </label>
              {cycleRows.map((c) => (
                <label
                  key={c.key}
                  className="flex items-center gap-2 text-sm text-navy cursor-pointer"
                >
                  <input
                    type="radio"
                    name="cycle"
                    className="text-coral focus:ring-coral"
                    checked={cycle === c.key}
                    onChange={() => setCycle(c.key)}
                  />
                  {c.label} ({c.count})
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {(categorie !== null || cycle !== null) && (
          <button
            type="button"
            onClick={() => {
              setCategorie(null);
              setCycle(null);
            }}
            className="text-sm font-semibold text-coral hover:underline"
          >
            Réinitialiser les filtres
          </button>
        )}
      </aside>

      <div>
        <h2 className="text-2xl font-display font-bold text-navy mb-6">
          Écoles trouvées ({total})
        </h2>

        {isLoading ? (
          <div className="grid sm:grid-cols-2 gap-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-white p-5 rounded-xl border border-orange-50 animate-pulse h-32"
              />
            ))}
          </div>
        ) : items.length > 0 ? (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              {items.map((ecole) => (
                <Link
                  key={ecole.id}
                  href={`/ecoles/${ecole.id}`}
                  className="bg-white rounded-xl p-5 border border-orange-50 hover:border-coral/30 hover:shadow-md transition-all group flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-start justify-between mb-2">
                      <div className="w-10 h-10 bg-cream rounded-full flex items-center justify-center text-navy/40">
                        <Building2 size={18} />
                      </div>
                      {ecole.typeEnseignement && (
                        <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded">
                          {ecole.typeEnseignement}
                        </span>
                      )}
                    </div>
                    <h4 className="font-bold text-navy mb-2 group-hover:text-coral transition-colors">
                      {ecole.denomination}
                    </h4>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1 text-navy/60">
                      <MapPin size={14} /> {ecole.communeNom ?? "·"}
                    </span>
                    <ChevronRight
                      size={16}
                      className="text-coral opacity-0 group-hover:opacity-100 transition-opacity"
                    />
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-8 flex flex-col items-center gap-3">
              <p className="text-sm text-navy/60">
                {items.length} école{items.length > 1 ? "s" : ""} sur {total}
              </p>
              {hasNextPage && (
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="px-6 py-3 rounded-full bg-coral text-white font-semibold hover:bg-coral/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isFetchingNextPage ? "Chargement…" : "Charger plus"}
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="bg-white rounded-xl p-8 text-center border border-orange-50 text-navy/60">
            Aucune école trouvée.
          </div>
        )}
      </div>
      </div>
    </>
  );
}
